'use client';

import { Loader2, UploadCloud } from 'lucide-react';
import { toast } from 'sonner';
import { useFileUpload } from '../../hooks/useFileUpload';
import { trpc } from '../../lib/trpc';
import { AttachmentList } from '../attachment-list';
import { FileDropzone } from '../file-dropzone';

interface TaskFileAttachmentsProps {
  taskId: number;
  isReadOnly?: boolean;
}

export function TaskFileAttachments({ taskId, isReadOnly = false }: TaskFileAttachmentsProps) {
  const utils = trpc.useUtils();
  const { data: files, isLoading } = trpc.files.getForTarget.useQuery({
    targetType: 'task',
    targetId: taskId,
  });
  const { mutateAsync: createAssociation } = trpc.files.createAssociation.useMutation();
  const { mutateAsync: dissociate } = trpc.files.dissociate.useMutation();
  const { mutateAsync: updateFile } = trpc.files.update.useMutation();
  const { uploadAll, isUploading } = useFileUpload();

  const invalidate = () =>
    utils.files.getForTarget.invalidate({ targetType: 'task', targetId: taskId });

  const handleUpload = async (droppedFiles: File[]) => {
    const promise = (async () => {
      const fileIds = await uploadAll(droppedFiles);
      if (fileIds.length === 0) return;

      await Promise.all(fileIds.map((fileId) => createAssociation({ fileId, taskId })));
      await invalidate();
    })();

    toast.promise(promise, {
      loading: 'Uploading files...',
      success: 'Files uploaded successfully',
      error: 'Failed to upload files',
    });
  };

  const handleRemove = async (associationId: number) => {
    try {
      await dissociate({ id: associationId });
      await invalidate();
      toast.success('File removed');
    } catch {
      toast.error('Failed to remove file');
    }
  };

  const handleRename = async (fileId: number, newName: string) => {
    try {
      await updateFile({ id: fileId, label: newName });
      await invalidate();
      toast.success('File renamed');
    } catch {
      toast.error('Failed to rename file');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="size-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {!isReadOnly && (
        <FileDropzone
          onFilesDrop={handleUpload}
          noClick={false}
          className="border-2 border-dashed rounded-xl p-6 text-center hover:bg-muted/50 transition-all cursor-pointer bg-muted/20"
        >
          <div className="flex flex-col items-center gap-2">
            {isUploading ? (
              <Loader2 className="size-8 animate-spin text-primary" />
            ) : (
              <UploadCloud className="size-8 text-muted-foreground/60" />
            )}
            <div className="space-y-0.5">
              <p className="text-xs font-semibold">Upload files</p>
              <p className="text-[11px] text-muted-foreground">Drag and drop or click to browse</p>
            </div>
          </div>
        </FileDropzone>
      )}

      {files && files.length > 0 ? (
        <AttachmentList
          attachments={files.map((file) => ({
            type: 'remote',
            fileId: file.id,
            fileName: file.fileName,
            fileType: file.fileType,
            associationId: file.associationId,
            viewUrl: file.viewUrl,
          }))}
          onRemove={isReadOnly ? undefined : (index) => handleRemove(files[index].associationId)}
          onRename={
            isReadOnly ? undefined : (index, newName) => handleRename(files[index].id, newName)
          }
          className="bg-transparent p-0 border-none flex flex-row flex-wrap gap-3"
        />
      ) : (
        <p className="text-xs text-muted-foreground italic">No files attached.</p>
      )}
    </div>
  );
}
